import { get, map } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { getSelectedSecurities, getToolbarActive, getCurrentPageSelectionStatus, getFoundSecurities, getWatchlistsMenuVisible, getSecuritiesCount, getSort, getErrorMessage } from "./selectors"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "model/compositeSecurity"
import { getTableColumns, getFilteredColumnsSelector, getContentSets, getAvailableColumns } from "selectors/securities-table"
import { getMissingDataFiles, betasSubQuery } from "selectors/missingDataFiles"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { mapFactorExposureNegativeBoolOperator } from "helpers/filters"
import { getIsOpPro } from "modules/account-root/selectors"
import { Creators as notificationCreators } from "modules/notifications/model"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    page: props.page,
    sort: getSort(state, props),
    filter: props.filter,
    filterId: props.filterId,
    columnSearches: get(state, "modules.securitiesFinderResults.columnSearches"),
    contentSets: getContentSets(state),
    missingDataFiles: getMissingDataFiles(state),
    classifications: getClassifications(state),
    isOpPro: getIsOpPro(state),
    currency: get(props, "currency"),
    isHistoricalFactorSensitivityAvailable: get(props, "isHistoricalFactorSensitivityAvailable"),
  }),
  required: ["account", "modelId"],
  data: [{
    resource: "foundSecurities",
    passthrough: true,
    url: ({ modelId, isOpPro, filterId, filter, selectedDate, page, sort, contentSets, missingDataFiles, classifications, currency, isHistoricalFactorSensitivityAvailable }) => {
      if (!filterId || !missingDataFiles) {
        return null
      }

      filter = mapFactorExposureNegativeBoolOperator(filter)
      return {
        id: `${modelId}${filterId}${selectedDate}${page}${sort.direction}${sort.type}${sort.id}${isOpPro}${currency}`,
        query: `query ($modelId: String!, $filter: [SecuritySearchFilter!]!, $on: Date!, $skip: Int!, $sort: [SecuritySearchSort], $currency: Currency) {
          model (id: $modelId) {
            securitySearch (filter: $filter, on: $on, skip: $skip, sort: $sort, currency: $currency) {
              count
              securities {
                id
                assetClass
                ticker
                mic
                internalId
                description
                country
                currency
                ${generateClassificationQueryForContributors("GICS", classifications)}
                averageDailyVolume
                marketCapitalization
                factorExposure {
                  id
                  zScore
                }
                ${contentSets.length > 0 ? map(contentSets, ({ id, escapedId }) => `${escapedId}: factorExposure (contentSetId: "${id}") { id, zScore } `) : ""}
                ${!isHistoricalFactorSensitivityAvailable ? "" : `historicalFactorSensitivity: factorSensitivity(type: ${FACTOR_SENSITIVITY_TYPES.HISTORICAL}) {
                  factors {
                    id
                    sensitivity
                  }
                }`}
                ${isOpPro ? "" : `
                risk {
                  standardDeviation {
                    total
                  }
                  varianceDecomposition {
                    summary {
                      factors
                      specific
                    }
                  }
                }
                predictedFactorSensitivity: factorSensitivity(type: ${FACTOR_SENSITIVITY_TYPES.PREDICTED}) {
                  factors {
                    id
                    sensitivity
                  }
                }`}
                ${betasSubQuery(missingDataFiles)}
              }
            }
          }
        }`,
        variables: {
          modelId,
          filter,
          on: selectedDate,
          skip: page * 200,
          sort: sort.getter(sort.id, sort.direction),
          currency,
        },
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile2,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
    addNotification: notificationCreators.add,
  },
  mapStateToProps: (state, props) => ({
    count: getSecuritiesCount(state, props),
    foundSecurities: getFoundSecurities(state, props),
    columns: getTableColumns(state, props),
    columnSelectorOpen: get(state, "modules.securitiesFinderResults.columnSelectorOpen"),
    status: get(state, "global.data.foundSecurities.status"),
    selectedSecurities: getSelectedSecurities(state, props),
    toolbarActive: getToolbarActive(state, props),
    currentPageSelectionStatus: getCurrentPageSelectionStatus(state, props),
    filteredColumnsSelector: getFilteredColumnsSelector(state, props),
    columnSearches: get(state, "modules.securitiesFinderResults.columnSearches"),
    watchlistsMenuVisible: getWatchlistsMenuVisible(state, props),
    errorMessage: getErrorMessage(state),
    availableColumns: getAvailableColumns(state),
  }),
})
